import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Backdrop } from '@spotify-internal/creator-tape';
import {
  gray10,
  gray20,
  gray90,
  screenMdMin,
  screenSmMax,
} from '@spotify-internal/tokens';
import { useViewport, Viewport } from '../../../shared/lib/useViewport';
import { LoadingIndicator } from '../../../shared/components/LoadingIndicator';
import { ActionPanelPortal } from '../ActionPanelPortal';
import { useActionPanelState } from '../';

/* TODO: Maybe move some of these styles to Creator Tape */

const ActionPanelBackdrop = styled(Backdrop)`
  display: ${props => (props.show ? undefined : 'none')};
`;

const Shell = styled.div<{ show: boolean }>`
  width: 344px;
  background: ${props => (props.theme.app === 'dark' ? gray10 : 'white')};
  flex-shrink: 0;
  border-left: 1px solid
    ${props => (props.theme.app === 'dark' ? gray20 : gray90)};

  @media (max-width: ${screenSmMax}) {
    position: fixed;
    height: 100%;
    top: 0;
    right: ${props => (props.show ? '0' : '-200px')};
  }

  @media (min-width: ${screenMdMin}) {
    position: sticky;
    overflow: hidden;
    margin-top: -24px; /* body padding */
    margin-bottom: -64px; /* footer top-margin */
    top: 80px; /* navbar height */
    max-height: calc(100vh - 80px); /* navbar height */
    height: calc(100% + 88px); /* footer margin (64px) + navbar margin (24px) */
  }
`;

type Props = {
  children?: JSX.Element | null;
};
export function ActionPanelShell(props: Props) {
  const viewport = useViewport();
  const isSmallScreen = viewport === Viewport.SM || viewport === Viewport.XS;
  const { actionPanelVisible, hideActionPanel } = useActionPanelState();

  if (!props.children) {
    return null;
  }

  /* Small screen layout: Use Backdrop and set Action Panel to overlay page content */
  if (isSmallScreen) {
    return (
      <React.Suspense fallback={<LoadingIndicator />}>
        <ActionPanelPortal>
          <ActionPanelBackdrop
            show={actionPanelVisible}
            onClose={() => hideActionPanel()}
          >
            <Shell show={actionPanelVisible} {...props}>
              {props.children}
            </Shell>
          </ActionPanelBackdrop>
        </ActionPanelPortal>
      </React.Suspense>
    );
  }
  /* Large screen layout: Action Panel appears next to page content */
  return (
    <React.Suspense fallback={<LoadingIndicator />}>
      <ActionPanelPortal>
        <Shell show={actionPanelVisible} {...props}>
          {props.children}
        </Shell>
      </ActionPanelPortal>
    </React.Suspense>
  );
}

ActionPanelShell.propTypes = {
  children: PropTypes.node,
};
